import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import OnboardingProgress from '../components/onboarding-progress'
import { PendingReviewsWidget } from '../components/content-review/PendingReviewsWidget'
import { DataQualityWidget } from '../components/home/DataQualityWidget'
import { KnowledgeGapsWidget } from '../components/home/KnowledgeGapsWidget'
import { MySuggestionsWidget } from '../components/home/MySuggestionsWidget'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { Code, Copy, Check, Chrome, Gift, X } from 'lucide-react'
import { toast } from '@/components/ui/use-toast'
import { isProduction, isStaging } from '../config/api'
import { useSite } from '../contexts/SiteContext'
import { UsageWidget } from '../components/usage/UsageWidget'
import { trackActivity } from '../utils/tracking'

const Home: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedSite, sites, loading } = useSite()
  const [showIntegrationDialog, setShowIntegrationDialog] = useState(false)
  const [copiedCode, setCopiedCode] = useState(false)
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false)
  
  // Check if user just signed up
  useEffect(() => {
    if (location.state?.fromSignup) {
      setShowWelcomeBanner(true)
      // Clear the state to prevent showing again on refresh
      window.history.replaceState({}, document.title)
    }
  }, [location.state])
  
  // Track visit to admin page when component mounts
  useEffect(() => {
    if (selectedSite) {
      trackActivity('visit_admin', selectedSite._id || selectedSite.id);
    }
  }, [selectedSite])

  const generateJavaScriptCode = (siteId: string) => {
    return `<!-- Add this before </body> tag -->
<script src="https://pixel.pagemind.ai/pagemind.js"></script>
<script>
  PageMind.init({
    siteId: "${siteId}"
  });
</script>`;
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(true);
      toast({
        title: 'Copied!',
        description: 'JavaScript code copied to clipboard',
      });
      setTimeout(() => setCopiedCode(false), 3000);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Welcome Banner for New Signups */}
      {showWelcomeBanner && (
        <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg p-6 shadow-lg">
          <button
            onClick={() => setShowWelcomeBanner(false)}
            className="absolute top-2 right-2 text-white/80 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-start space-x-4">
            <Gift className="h-8 w-8 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold mb-2">Welcome to PageMind! 🎉</h2>
              <p className="text-lg mb-3">
                Your account has been created successfully! You've earned bonus conversations through our referral program.
              </p>
              <p className="text-white/90">
                Let's get your website set up so you can start engaging with your visitors using AI.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div>
        <h1 className="text-3xl font-bold mb-6">Welcome to PageMind Admin</h1>
      </div>
      
      {/* Onboarding Journey - Most Prominent Element */}
      <OnboardingProgress />
      
      {/* Integration Section */}
      <Card>
        <CardHeader>
          <CardTitle>Get Started with PageMind</CardTitle>
          <CardDescription>
            Add PageMind to your website or browse any site with our Chrome extension
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => setShowIntegrationDialog(true)}
              className="flex-1"
              variant="outline"
            >
              <Code className="mr-2 h-4 w-4" />
              See Integration Code
            </Button>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-md text-center">
                Install PageMind <span className="font-bold">Chrome Extension</span> by going to{' '}
                <a 
                  href="chrome://extensions" 
                  className="text-primary hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    toast({
                      title: 'Chrome Extensions',
                      description: 'Please navigate to chrome://extensions in your browser manually',
                    });
                  }}
                >
                  chrome://extensions
                </a>
                {' '}or{' '}
                <a 
                  href="https://chromewebstore.google.com/search/pagemind" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  https://chromewebstore.google.com/search/pagemind
                </a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Usage Widget - Show if site is selected */}
      {selectedSite && (
        <div className="mb-8">
          <UsageWidget siteId={selectedSite.id} />
        </div>
      )}

      {/* Content Management Widgets */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <PendingReviewsWidget />
        <DataQualityWidget />
        <KnowledgeGapsWidget />
        <MySuggestionsWidget />
      </div>

      {/* Quick Actions - Smaller, Less Prominent */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="p-4">
              <CardTitle className="text-sm">Agents</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Button 
                onClick={() => navigate('/agents/dashboard')} 
                variant="secondary"
                size="sm"
                className="w-full"
              >
                View
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="p-4">
              <CardTitle className="text-sm">Sites</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Button 
                onClick={() => navigate('/sites')} 
                variant="secondary"
                size="sm"
                className="w-full"
              >
                View
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="p-4">
              <CardTitle className="text-sm">Monitoring</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Button 
                onClick={() => navigate('/monitoring')} 
                variant="secondary"
                size="sm"
                className="w-full"
              >
                View
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="p-4">
              <CardTitle className="text-sm">Sessions</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Button 
                onClick={() => navigate('/active-sessions')} 
                variant="secondary"
                size="sm"
                className="w-full"
              >
                View
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="p-4">
              <CardTitle className="text-sm">Analytics</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Button 
                onClick={() => navigate('/behavior')} 
                variant="secondary"
                size="sm"
                className="w-full"
              >
                View
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="p-4">
              <CardTitle className="text-sm">Insights</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Button 
                onClick={() => navigate('/insights')} 
                variant="secondary"
                size="sm"
                className="w-full"
              >
                View
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Integration Code Dialog */}
      <Dialog open={showIntegrationDialog} onOpenChange={setShowIntegrationDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>JavaScript Integration</DialogTitle>
            <DialogDescription>
              Copy this code and add it to your website before the closing &lt;/body&gt; tag
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {!selectedSite && (
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <p className="text-yellow-600 dark:text-yellow-400">
                  Please select a site from the dropdown at the top of the page to see its integration code.
                </p>
              </div>
            )}
            
            {/* JavaScript Code Section */}
            <div>
              <h4 className="text-sm font-medium mb-2">JavaScript Code</h4>
              <div className="relative">
                <pre className="bg-muted text-muted-foreground p-4 rounded-md overflow-x-auto text-sm font-mono">
                  <code>{selectedSite ? generateJavaScriptCode(selectedSite._id || selectedSite.id || "YOUR_SITE_ID") : generateJavaScriptCode("YOUR_SITE_ID")}</code>
                </pre>
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(selectedSite ? generateJavaScriptCode(selectedSite._id || selectedSite.id || "YOUR_SITE_ID") : generateJavaScriptCode("YOUR_SITE_ID"))}
                >
                  {copiedCode ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Site Details Section */}
            {selectedSite && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-3">Site Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex">
                    <span className="font-medium w-24">Site ID:</span>
                    <span className="text-muted-foreground font-mono">{selectedSite._id || selectedSite.id}</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium w-24">URL:</span>
                    <span className="text-muted-foreground">{selectedSite.url}</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium w-24">Status:</span>
                    <span className="text-green-600 dark:text-green-400">{selectedSite.status}</span>
                  </div>
                  {selectedSite.type && (
                    <div className="flex">
                      <span className="font-medium w-24">Type:</span>
                      <span className="text-muted-foreground">{selectedSite.type}</span>
                    </div>
                  )}
                </div>
              </div>
            )}


            {sites.length > 1 && (
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> You have {sites.length} sites. Use the site selector at the top of the page to switch between sites, or visit the{' '}
                  <button
                    onClick={() => {
                      setShowIntegrationDialog(false);
                      navigate('/sites');
                    }}
                    className="text-primary hover:underline"
                  >
                    Sites page
                  </button>
                  {' '}for more options.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Home